import { useQuery } from "@tanstack/react-query";

import type { components } from "../api/schema";
import { apiClient } from "../lib/apiClient";
import { ApiRequestError } from "../lib/errors";
import { queryKeys } from "../lib/queryKeys";
import {
  Copyable,
  InlineError,
  KeyValue,
  Page,
  Section,
  Skeleton,
} from "../ui";

type MeResponse = components["schemas"]["MeResponse"];

function optionalText(value: string | null | undefined): string {
  const trimmed = value?.trim();
  return trimmed && trimmed.length > 0 ? trimmed : "—";
}

function profileRows(me: MeResponse) {
  return [
    {
      label: "Display name",
      value: optionalText(me.user.display_name),
    },
    { label: "Email", value: optionalText(me.user.email) },
    {
      label: "User ID",
      value: <Copyable label="User ID" value={me.user.id} />,
    },
    {
      label: "Organization",
      value: optionalText(me.organization.name),
    },
  ];
}

export function ProfileSettings() {
  const me = useQuery({
    queryKey: queryKeys.me,
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/v1/me");
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
  });

  return (
    <Page variant="config">
      {/* Displays /v1/me only. Every field here is read-only because the API
          exposes no write: /v1/me, /v1/users and /v1/users/{id} are GET-only,
          so there is nothing to edit against. `display_name` is seeded from
          the IdP claims at first login and never refreshed
          (AppUserRepository.get_or_create returns the stored record), so a
          user-editable name WOULD stick — it needs a write endpoint first.
          The page is visibly read-only, so it carries no header description
          and no section heading. */}
      {me.isPending ? (
        // Known-shape loading (§4c): the KeyValue rows skeleton, not a spinner.
        <Skeleton label="Loading profile" rows={4} shape="detail-field" />
      ) : null}
      {me.isError ? <InlineError>{me.error.message}</InlineError> : null}
      {!me.isPending && !me.isError ? (
        <Section>
          <KeyValue rows={profileRows(me.data)} />
        </Section>
      ) : null}
    </Page>
  );
}
